import { type ReactNode, type RefObject, useState } from "react";

type FileDropzoneProps = {
  /** Accessible name for the underlying file input (kept stable for a11y + tests). */
  ariaLabel: string;
  accept: string;
  multiple?: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  onFilesPicked: (files: FileList | null) => void;
  /** Optional hint rendered under the primary call-to-action. */
  hint?: ReactNode;
};

/**
 * Drag-and-drop upload area (L0-U7) wrapping a real, labelled `<input type=file>`.
 *
 * The input keeps its accessible name (`ariaLabel`) and stays in the DOM so
 * assistive tech and tests can drive it directly; the visible affordance is the
 * drop zone, and the whole zone is a `<label>` so a click opens the file dialog.
 * Dropped files are forwarded through the same `onFilesPicked` callback the
 * input's change event uses, so the page has a single intake path.
 */
export function FileDropzone({
  ariaLabel,
  accept,
  multiple = false,
  inputRef,
  onFilesPicked,
  hint,
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <label
      data-testid="file-dropzone"
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        onFilesPicked(e.dataTransfer.files);
      }}
      className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-control border-2 border-dashed px-6 py-10 text-center transition-colors ${
        isDragging
          ? "border-primary bg-primary/5"
          : "border-outline bg-surface-container-low hover:border-primary/60 hover:bg-surface-container-high"
      }`}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        className="h-8 w-8 text-on-surface-variant"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 7.5L12 3m0 0L7.5 7.5M12 3v13.5"
        />
      </svg>
      <span className="text-sm font-medium text-on-surface">
        Drag and drop your CSV here, or{" "}
        <span className="text-primary underline-offset-2 hover:underline">browse</span>
      </span>
      {hint !== undefined && (
        <span className="text-xs text-on-surface-variant">{hint}</span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => onFilesPicked(e.target.files)}
        aria-label={ariaLabel}
        className="sr-only"
      />
    </label>
  );
}
